import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import collectionRepository from '@/features/collections/repositories/CollectionRepository';
import pageRepository from '@/features/pages/repositories/PageRepository';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import type { Page } from '@/features/pages/types/page.types';
import { invalidateCollectionQueries, pageKeys } from '@/shared/query/queryKeys';

export function usePageTitleMutation() {
  const queryClient = useQueryClient();
  const setIsDirty = usePageStore(state => state.setIsDirty);

  return useCallback(
    async (id: string, title: string) => {
      if (!usePageStore.getState().editable) return;

      await pageRepository.updatePage(id, { title });
      setIsDirty(true);

      const detailKey = pageKeys.detail(id);
      const cached = queryClient.getQueryData<Page>(detailKey);
      if (cached) {
        queryClient.setQueryData<Page>(detailKey, { ...cached, title, updatedAt: Date.now() });
      }

      if (cached?.collectionId) {
        void collectionRepository.updateCollection(cached.collectionId, {
          updatedAt: Date.now(),
        });
        void invalidateCollectionQueries(queryClient);
      }

      void queryClient.invalidateQueries({ queryKey: pageKeys.lists() });
    },
    [queryClient, setIsDirty]
  );
}
